import React, { useContext, useState } from 'react';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import { ColWithGap } from '@components/styles';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import styled from 'styled-components';
import { InputTextarea } from 'primereact/inputtextarea';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import { getCrnFormatDescription, validateCrn } from '@utils/crnValidation.ts';
import { findErrorMessage } from '@utils/validationUtils.ts';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { getTimezoneForCountryCode3 } from '@utils/countryTimezone.ts';
import dayjs from 'dayjs';

enum RegistrationStage {
  NEW = 'NEW',
  COMPANY_EXISTS = 'COMPANY_EXISTS',
  COMPANY_DETAILS = 'COMPANY_DETAILS',
  FINISHED = 'FINISHED',
}

interface ComponentProps {
  registrationCode?: string;
}

interface RegistrationForm {
  countryCode: string;
  crn: string;
  registrationCode: string;
  vat: string;
  companyName: string;
  streetNr: string;
  city: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  phone: string;
  timezoneId: string;
}

const createInitialData = (registrationCode?: string): RegistrationForm => {
  return {
    countryCode: 'CZE',
    crn: '',
    registrationCode: registrationCode || '',
    vat: '',
    companyName: '',
    streetNr: '',
    city: '',
    zipCode: '',
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    timezoneId: getTimezoneForCountryCode3('CZE') || dayjs.tz.guess(),
  };
};

interface ContactForm {
  email: string;
  text?: string;
}

const RegistrationDetail: React.FC<ComponentProps> = ({ registrationCode }) => {
  const { tr } = useTranslationLgs();
  const { checkCompanyIfExists, registerCompany, contactRingilCompanyForRegistration } = useContext(ApiContext);
  const [registrationCodeInValid, setRegistrationCodeInValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [registrationStage, setRegistrationStage] = useState<RegistrationStage>(RegistrationStage.NEW);

  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

  const registrationSchema = registrationStage === RegistrationStage.COMPANY_DETAILS ? yup.object().shape({
      countryCode: yup.string().required(tr('Registration.registrationFormCountryRequired', 'Country is required')),
      crn: yup
        .string()
        .required(tr('validationCrn.required', 'CRN is required'))
        .test('crn-format', function(value) {
          const { countryCode } = this.parent;
          if (!value || !countryCode) return true;
          const isValid = validateCrn(value, countryCode);
          if (!isValid) {
            const formatDesc = getCrnFormatDescription(countryCode, tr);
            return this.createError({
              message: formatDesc
                ? tr('validationCrn.crnInvalidFormat', `Invalid CRN format (expected format: {{format}})`, { format: formatDesc })
                : tr('validationCrn.crnInvalid', `Invalid CRN format`),
            });
          }
          return true;
        }),
      registrationCode: yup.string().required(tr('Registration.registrationFormRegistrationCodeRequired', 'Registration code is required')),
      vat: yup.string().required(tr('Registration.registrationFormVatRequired', 'VAT is required')),
      companyName: yup.string().required(tr('Registration.registrationFormCompanyNameRequired', 'Company name is required')),
      streetNr: yup.string().required(tr('Registration.registrationFormStreetRequired', 'Street and number is required')),
      city: yup.string().required(tr('Registration.registrationFormCityRequired', 'City is required')),
      zipCode: yup.string().required(tr('Registration.registrationFormZipCodeRequired', 'ZIP code is required')),
      firstName: yup.string().required(tr('Registration.registrationFormFirstNameRequired', 'First name is required')),
      lastName: yup.string().required(tr('Registration.registrationFormLastNameRequired', 'Last name is required')),
      email: yup.string().required(tr('Registration.registrationFormEmailRequired', 'Email is required'))
        .email(tr('Registration.registrationFormEmailInvalid', 'Please enter a valid email address')),
      confirmEmail: yup.string().required(tr('Registration.registrationFormConfirmEmailRequired', 'Please confirm your email'))
        .email(tr('Registration.registrationFormEmailInvalid', 'Please enter a valid email address'))
        .oneOf([yup.ref('email')], tr('Registration.registrationFormEmailsMustMatch', 'Emails must match')),
      phone: yup.string().required(tr('Registration.registrationFormPhoneRequired', 'Phone is required')),
      timezoneId: yup.string().required(tr('Registration.registrationFormTimezoneRequired', 'Time zone is required')),
    }) :
    yup.object().shape({
      countryCode: yup.string().required(tr('Registration.registrationFormCountryRequired', 'Country is required')),
      crn: yup
        .string()
        .required(tr('validationCrn.required', 'CRN is required'))
        .test('crn-format', function(value) {
          const { countryCode } = this.parent;
          if (!value || !countryCode) return true;
          const isValid = validateCrn(value, countryCode);
          if (!isValid) {
            const formatDesc = getCrnFormatDescription(countryCode, tr);
            return this.createError({
              message: formatDesc
                ? tr('validationCrn.crnInvalidFormat', `Invalid CRN format (expected format: {{format}})`, { format: formatDesc })
                : tr('validationCrn.crnInvalid', `Invalid CRN format`),
            });
          }
          return true;
        }),
      registrationCode: yup.string().required(tr('Registration.registrationFormRegistrationCodeRequired', 'Registration code is required')),
    });


  const registrationForm = useForm<RegistrationForm>(
    registrationSchema,
    createInitialData(registrationCode),
    d => saveRegistrationForm(d),
    false,
    false,
    null,
    'scroll',
  );

  React.useEffect(() => {
    registrationForm.useProduce(d => {
      d.registrationCode = registrationCode || '';
    });
  }, [registrationCode]);

  // Keep the default time zone in sync with the chosen country. The country is only editable in the
  // NEW stage (it is disabled once we reach COMPANY_DETAILS where the picker is shown), so this never
  // clobbers a manual choice the user has made in the picker.
  React.useEffect(() => {
    if (registrationStage !== RegistrationStage.NEW) return;
    const countryTimezone = getTimezoneForCountryCode3(registrationForm.values.countryCode);
    if (countryTimezone) {
      registrationForm.useProduce(d => {
        d.timezoneId = countryTimezone;
      });
    }
  }, [registrationForm.values.countryCode, registrationStage]);

  const saveRegistrationForm = (data: RegistrationForm) => {
    setLoading(true);
    if (registrationStage === RegistrationStage.NEW) {
      checkCompanyIfExists({
          crn: registrationForm.values.crn,
          country: registrationForm.values.countryCode,
          code: registrationForm.values.registrationCode,
        },
        data => {
          if (!data.codeIsOk) {
            setRegistrationCodeInValid(true);
          } else if (data.isExists) {
            setRegistrationStage(RegistrationStage.COMPANY_EXISTS);
          } else {
            setRegistrationStage(RegistrationStage.COMPANY_DETAILS);
          }
          setLoading(false);
        });
    }
    if (registrationStage === RegistrationStage.COMPANY_DETAILS) {
      registerCompany(
        registrationForm.values.registrationCode,
        {
          country: registrationForm.values.countryCode,
          crn: registrationForm.values.crn,
          vat: registrationForm.values.vat,
          companyName: registrationForm.values.companyName,
          streetNr: registrationForm.values.streetNr,
          city: registrationForm.values.city,
          zipCode: registrationForm.values.zipCode,
          adminFirstName: registrationForm.values.firstName,
          adminLastName: registrationForm.values.lastName,
          adminEmail: registrationForm.values.email,
          phone: registrationForm.values.phone,
          defaultTimezoneId: registrationForm.values.timezoneId,
        },
        () => {
          setLoading(false);
          setRegistrationStage(RegistrationStage.FINISHED);
          setBeValidationErrors([])
        },
        null,
        {
          onValidationFailed: e => {
            setLoading(false);
            setBeValidationErrors(e);
          },
        },
      );

    }
  };

  const contactSchema = yup.object().shape({
    email: yup.string()
      .required(tr('Registration.contactFormEmailRequired', 'Email is required'))
      .email(tr('Registration.contactFormEmailInvalid', 'Please enter a valid email address')),
  });

  const contactForm = useForm<ContactForm>(
    contactSchema,
    {
      email: '',
      text: '',
    },
    d => saveContactForm(d),
    false,
    false,
    null,
    'scroll',
  );

  const saveContactForm = (data: ContactForm) => {
    contactRingilCompanyForRegistration({
      crn: registrationForm.values.crn,
      country: registrationForm.values.countryCode,
      code: registrationForm.values.registrationCode,
      contactEmail: data.email,
      registerMessage: data.text,
    }, () => {
      window.location.reload();
    });
  };

  React.useEffect(() => {
    if (registrationForm.validationErrors && registrationForm.validationErrors.length > 0) {
      console.log('Validation errors:', registrationForm.validationErrors.map(error => ({
        path: error.path,
        message: error.message,
      })));
    }
  }, [registrationForm.validationErrors]);

  return (
    <>
      <div className={'flex flex-col gap-2'}>
        <BreadCrumbComponent items={[{ label: tr(`Registration.breadCrumbLabel`, `Registration`) }]} />
        <BackendValidationComponent />
        {(registrationStage === RegistrationStage.NEW || registrationStage === RegistrationStage.COMPANY_DETAILS) && (
          <>
            <div className={'flex flex-row gap-4 items-center'}>
              <COHeadline variant={'h2'} title={tr('Registration.title', 'Registration')} />
            </div>

            <ColWithGap style={{ width: '480px', marginTop: '1rem' }}>
              <Field
                label={tr(`Registration.registrationFormCountry`, `Country`)}
                required
                errorMessage={findErrorMessage(registrationForm.validationErrors, `countryCode`, tr)}
                disabled={registrationStage === RegistrationStage.COMPANY_DETAILS}
              >
                <InputCountry
                  value={registrationForm.values.countryCode}
                  onChange={v => {
                    registrationForm.useProduce(d => {
                      d.countryCode = v;
                    });
                  }}
                />
              </Field>
              <Field
                label={tr(`Registration.registrationFormCrn`, `CRN`)}
                required
                errorMessage={findErrorMessage(registrationForm.validationErrors, `crn`, tr)}
                disabled={registrationStage === RegistrationStage.COMPANY_DETAILS}
              >
                <InputText
                  value={registrationForm.values.crn}
                  onChange={v => {
                    registrationForm.useProduce(d => {
                      d.crn = v.target.value;
                    });
                  }}
                />
              </Field>

              {registrationStage === RegistrationStage.NEW && (
                <>
                  <COHeadline title={tr(`Registration.titleRegistrationCode`, 'Activation code')}
                              variant={'h3'}
                  />
                  <Description>
                    {tr('Registration.codeDescription', 'Code sent by your partner. Use it to activate your company and assigned modules')}
                  </Description>
                </>
              )}
              <Field
                label={tr(`Registration.registrationFormRegistrationCode`, `Registration Code`)}
                required
                errorMessage={
                  registrationCodeInValid
                    ? tr('Registration.invalidRegistrationCode', 'Invalid registration code')
                    : findErrorMessage(registrationForm.validationErrors, 'registrationCode', tr)
                }
                disabled={registrationStage === RegistrationStage.COMPANY_DETAILS}
              >
                <InputText
                  value={registrationForm.values.registrationCode}
                  onChange={v => {
                    setRegistrationCodeInValid(false);
                    registrationForm.useProduce(d => {
                      d.registrationCode = v.target.value;
                    });
                  }}
                />
              </Field>
              {registrationStage === RegistrationStage.COMPANY_DETAILS && (
                <>
                  <Field
                    label={tr(`Registration.registrationFormVat`, `VAT`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `vat`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.vat}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.vat = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormCompanyName`, `Company Name`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `companyName`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.companyName}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.companyName = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormStreetNr`, `Streen and number`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `streetNr`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.streetNr}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.streetNr = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormCity`, `City`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `city`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.city}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.city = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormZipCode`, `ZIP Code`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `zipCode`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.zipCode}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.zipCode = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormTimezone`, `Time zone`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `timezoneId`, tr)}
                  >
                    <InputTimezone
                      useBrowserTimezones
                      classNameWrapper="w-full"
                      value={registrationForm.values.timezoneId}
                      code={registrationForm.values.countryCode}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.timezoneId = v;
                        });
                      }}
                    />
                  </Field>
                  <COHeadline title={tr(`Registration.titleAdministrationPerrson`, 'Administration person')}
                              variant={'h3'}
                  />
                  <Field
                    label={tr(`Registration.registrationFormFirstName`, `First Name`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `firstName`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.firstName}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.firstName = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormLastName`, `Last Name`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `lastName`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.lastName}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.lastName = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormEmail`, `Email`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `email`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.email}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.email = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormConfirmEmail`, `Confirm Email`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `confirmEmail`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.confirmEmail}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.confirmEmail = v.target.value;
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={tr(`Registration.registrationFormPhone`, `Phone`)}
                    required
                    errorMessage={findErrorMessage(registrationForm.validationErrors, `phone`, tr)}
                  >
                    <InputText
                      value={registrationForm.values.phone}
                      onChange={v => {
                        registrationForm.useProduce(d => {
                          d.phone = v.target.value;
                        });
                      }}
                    />
                  </Field>
                </>
              )}

              <div className="pt-4">
                <RingilButton
                  label={tr(`Registration.registrationFormButtonCompanyCheck`, 'Continue')}
                  onClick={() => registrationForm.validateAndSend()}
                  mode={'filled'}
                  loading={loading}
                />
              </div>
            </ColWithGap>
          </>
        )}
        {registrationStage === RegistrationStage.COMPANY_EXISTS && (
          <>
            <div className={'flex flex-row gap-4 items-center'}>
              <COHeadline variant={'h2'} title={tr('Registration.titleCompanyExists', 'Company exists')} />
            </div>
            <div className={'flex flex-row gap-4 items-center pt-4'}>
              <COHeadline variant={'h3'} title={tr('Registration.titleAccessAsk', 'Ask to access company')} />
            </div>

            <ColWithGap style={{ width: '480px', marginTop: '1rem' }}>
              <Field
                label={tr(`Registration.contactFormEmail`, `Email`)}
                required
                errorMessage={findErrorMessage(contactForm.validationErrors, `email`, tr)}
              >
                <InputText
                  value={contactForm.values.email}
                  onChange={v => {
                    contactForm.useProduce(d => {
                      d.email = v.target.value;
                    });
                  }}
                />
              </Field>
              <Field
                label={tr(`Registration.contactFormText`, `Text`)}
                errorMessage={findErrorMessage(contactForm.validationErrors, `text`, tr)}
              >
                <InputTextarea
                  value={contactForm.values.text}
                  onChange={v => {
                    contactForm.useProduce(d => {
                      d.text = v.target.value;
                    });
                  }}
                  rows={8}
                />
              </Field>

              <div className="pt-4">
                <RingilButton
                  label={tr(`Registration.contactFormButtonSubmit`, 'Submit')}
                  onClick={() => contactForm.validateAndSend()}
                  mode={'filled'}
                  loading={loading}
                />
              </div>
            </ColWithGap>
          </>
        )}
        {registrationStage === RegistrationStage.FINISHED && (
          <>
            <div className={'flex flex-row gap-4 items-center'}>
              <COHeadline variant={'h2'}
                          title={tr('Registration.titleRegistrationFinished', 'Registration finished')} />
            </div>
            <div className={'flex flex-row gap-4 items-center pt-4'}>
              <COHeadline variant={'h3'}
                          title={tr('Registration.titleRegistrationSucceeded', 'Registration succeeded')} />
            </div>
            <Description>
              {tr('Registration.successMessage', 'You will receive an email with a password setup link within a few minutes. If you don\'t see it in your inbox, please check your spam folder.')}
            </Description>
          </>
        )}
      </div>
    </>
  );
};

export default RegistrationDetail;

const Description = styled.div`
  color: var(--content-secondary, #616061);
  max-width: 480px;

  /* Small Regular */
  font-family: "DM Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;